import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  ScrollView,
  TouchableOpacity,
  Image,
  Linking,
} from 'react-native';
import { ms, s, vs } from '../../utils/responsive';
import { NavigationProp } from '@react-navigation/native';
import backIcon from '../../assets/icons/back.png';

interface PrivacyPolicyScreenProps {
  navigation: NavigationProp<any>;
}

const PrivacyPolicyScreen: React.FC<PrivacyPolicyScreenProps> = ({ navigation }) => {
  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
          <Image source={backIcon} style={styles.backIcon} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Privacy Policy</Text>
        <View style={styles.placeholder} />
      </View>

      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        <Text style={styles.lastUpdated}>Last Updated: January 2, 2025</Text>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>1. Introduction</Text>
          <Text style={styles.paragraph}>
            Welcome to MyFinalKey ("we," "our," or "us"). We are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application and services.
          </Text>
          <Text style={styles.paragraph}>
            By using MyFinalKey, you agree to the collection and use of information in accordance with this policy. If you do not agree with our policies and practices, please do not use our application.
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>2. Information We Collect</Text>

          <Text style={styles.subsectionTitle}>2.1 Personal Information</Text>
          <Text style={styles.paragraph}>
            We collect information that you provide directly to us, including:
          </Text>
          <Text style={styles.bulletPoint}>• Name, email address, and contact information</Text>
          <Text style={styles.bulletPoint}>• Username and password</Text>
          <Text style={styles.bulletPoint}>• Profile information and preferences</Text>
          <Text style={styles.bulletPoint}>• Payment and billing information (processed securely through third-party payment processors)</Text>

          <Text style={styles.subsectionTitle}>2.2 Sensitive Information</Text>
          <Text style={styles.paragraph}>
            With your explicit consent, we may collect and store:
          </Text>
          <Text style={styles.bulletPoint}>• Medical records and health information</Text>
          <Text style={styles.bulletPoint}>• Financial account details</Text>
          <Text style={styles.bulletPoint}>• Legal documents and end-of-life planning information</Text>
          <Text style={styles.bulletPoint}>• Contact information for successors and keyholders</Text>
          <Text style={styles.bulletPoint}>• Passwords and login credentials for third-party accounts</Text>

          <Text style={styles.subsectionTitle}>2.3 Device Information</Text>
          <Text style={styles.paragraph}>
            We automatically collect certain information about your device, including:
          </Text>
          <Text style={styles.bulletPoint}>• Device type, operating system, and version</Text>
          <Text style={styles.bulletPoint}>• Unique device identifiers</Text>
          <Text style={styles.bulletPoint}>• Mobile network information</Text>
          <Text style={styles.bulletPoint}>• App usage data and crash reports</Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>3. How We Use Your Information</Text>
          <Text style={styles.paragraph}>
            We use the information we collect to:
          </Text>
          <Text style={styles.bulletPoint}>• Provide, maintain, and improve our services</Text>
          <Text style={styles.bulletPoint}>• Create and manage your account</Text>
          <Text style={styles.bulletPoint}>• Process your transactions and subscriptions</Text>
          <Text style={styles.bulletPoint}>• Send you technical notices, updates, and security alerts</Text>
          <Text style={styles.bulletPoint}>• Respond to your comments, questions, and customer service requests</Text>
          <Text style={styles.bulletPoint}>• Monitor and analyze usage patterns and trends</Text>
          <Text style={styles.bulletPoint}>• Detect, prevent, and address technical issues and security threats</Text>
          <Text style={styles.bulletPoint}>• Comply with legal obligations</Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>4. Data Security</Text>
          <Text style={styles.paragraph}>
            We implement industry-standard security measures to protect your information:
          </Text>
          <Text style={styles.bulletPoint}>• AES-256-CBC encryption with HMAC-SHA-256 authentication for sensitive data</Text>
          <Text style={styles.bulletPoint}>• Optional Recovery Code: when enabled, your master key is wrapped on your device so we cannot decrypt your vault without your code</Text>
          <Text style={styles.bulletPoint}>• Secure storage in HashiCorp Vault for passwords and credentials</Text>
          <Text style={styles.bulletPoint}>• Regular security audits and vulnerability assessments</Text>
          <Text style={styles.bulletPoint}>• Secure HTTPS connections for all data transmission</Text>
          <Text style={styles.bulletPoint}>• Multi-factor authentication options</Text>
          <Text style={styles.bulletPoint}>• Biometric authentication support (Face ID/Touch ID)</Text>
          <Text style={styles.paragraph}>
            However, no method of transmission over the internet or electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your information, we cannot guarantee absolute security.
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>5. Data Storage and Retention</Text>
          <Text style={styles.paragraph}>
            Depending on your subscription tier:
          </Text>
          <Text style={styles.bulletPoint}>• <Text style={styles.bold}>Free Tier:</Text> Only task completion data is stored</Text>
          <Text style={styles.bulletPoint}>• <Text style={styles.bold}>Local Storage:</Text> All data is stored locally on your device</Text>
          <Text style={styles.bulletPoint}>• <Text style={styles.bold}>Cloud Backup:</Text> Data is encrypted and backed up to secure cloud servers</Text>
          <Text style={styles.bulletPoint}>• <Text style={styles.bold}>Hard Copy:</Text> Additional physical backup of your encrypted data</Text>
          <Text style={styles.paragraph}>
            We retain your information for as long as your account is active or as needed to provide you services. You may request deletion of your data at any time through the app settings.
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>6. Information Sharing and Disclosure</Text>
          <Text style={styles.paragraph}>
            We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
          </Text>
          <Text style={styles.bulletPoint}>• <Text style={styles.bold}>With Successors/Keyholders:</Text> Only the information you explicitly choose to share</Text>
          <Text style={styles.bulletPoint}>• <Text style={styles.bold}>Service Providers:</Text> Third-party vendors who perform services on our behalf (payment processing, cloud hosting)</Text>
          <Text style={styles.bulletPoint}>• <Text style={styles.bold}>Legal Requirements:</Text> When required by law, court order, or government request</Text>
          <Text style={styles.bulletPoint}>• <Text style={styles.bold}>Business Transfers:</Text> In connection with any merger, sale, or acquisition</Text>
          <Text style={styles.bulletPoint}>• <Text style={styles.bold}>Protection of Rights:</Text> To protect our rights, privacy, safety, or property</Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>7. Your Rights and Choices</Text>
          <Text style={styles.paragraph}>
            You have the following rights regarding your personal information:
          </Text>
          <Text style={styles.bulletPoint}>• <Text style={styles.bold}>Access:</Text> Request access to your personal data</Text>
          <Text style={styles.bulletPoint}>• <Text style={styles.bold}>Correction:</Text> Request correction of inaccurate data</Text>
          <Text style={styles.bulletPoint}>• <Text style={styles.bold}>Deletion:</Text> Request deletion of your data</Text>
          <Text style={styles.bulletPoint}>• <Text style={styles.bold}>Export:</Text> Request a copy of your data in a portable format</Text>
          <Text style={styles.bulletPoint}>• <Text style={styles.bold}>Opt-out:</Text> Opt-out of marketing communications</Text>
          <Text style={styles.bulletPoint}>• <Text style={styles.bold}>Withdraw Consent:</Text> Withdraw consent for data processing</Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>8. Children's Privacy</Text>
          <Text style={styles.paragraph}>
            MyFinalKey is not intended for use by individuals under the age of 18. We do not knowingly collect personal information from children. If we discover that we have collected information from a child under 18, we will delete that information immediately.
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>9. International Data Transfers</Text>
          <Text style={styles.paragraph}>
            Your information may be transferred to and maintained on servers located outside of your state, province, country, or other governmental jurisdiction where data protection laws may differ. By using MyFinalKey, you consent to such transfers.
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>10. California Privacy Rights (CCPA)</Text>
          <Text style={styles.paragraph}>
            If you are a California resident, you have specific rights under the California Consumer Privacy Act (CCPA):
          </Text>
          <Text style={styles.bulletPoint}>• Right to know what personal information is collected</Text>
          <Text style={styles.bulletPoint}>• Right to know if personal information is sold or disclosed</Text>
          <Text style={styles.bulletPoint}>• Right to opt-out of the sale of personal information</Text>
          <Text style={styles.bulletPoint}>• Right to request deletion of personal information</Text>
          <Text style={styles.bulletPoint}>• Right to non-discrimination for exercising CCPA rights</Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>11. Changes to This Privacy Policy</Text>
          <Text style={styles.paragraph}>
            We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. You are advised to review this Privacy Policy periodically for any changes.
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>12. Contact Us</Text>
          <Text style={styles.paragraph}>
            If you have questions or concerns about this Privacy Policy or our privacy practices, please contact us at:
          </Text>
          <Text
            style={[styles.contactInfo, styles.contactLink]}
            onPress={() => Linking.openURL('mailto:privacy@myfinalkey.com')}
          >
            Email: privacy@myfinalkey.com
          </Text>
          <Text style={styles.contactInfo}>Address: Southlake, TX</Text>
          <Text
            style={[styles.contactInfo, styles.contactLink]}
            onPress={() => Linking.openURL('tel:4697372767')}
          >
            Phone: 469-737-2767
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>13. Data Breach Notification</Text>
          <Text style={styles.paragraph}>
            In the event of a data breach that may affect your personal information, we will notify you within 72 hours of becoming aware of the breach, in compliance with applicable data protection laws.
          </Text>
        </View>

        <View style={styles.bottomPadding} />
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F6F8',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: ms(15),
    paddingVertical: ms(15),
    backgroundColor: '#FFFFFF',
    borderBottomWidth: ms(1),
    borderBottomColor: '#E5E7EB',
  },
  backButton: {
    padding: ms(5),
  },
  backIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#1C2C4F',
  },
  headerTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
  },
  placeholder: {
    width: s(34),
  },
  content: {
    flex: 1,
    paddingHorizontal: ms(20),
  },
  lastUpdated: {
    fontSize: ms(12, 0.3),
    color: '#6B7280',
    fontStyle: 'italic',
    marginTop: ms(15),
    marginBottom: ms(10),
  },
  section: {
    marginBottom: ms(25),
  },
  sectionTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: '700',
    color: '#1C2C4F',
    marginBottom: ms(12),
  },
  subsectionTitle: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginTop: ms(10),
    marginBottom: ms(8),
  },
  paragraph: {
    fontSize: ms(14, 0.3),
    color: '#4B5563',
    lineHeight: ms(22, 0.3),
    marginBottom: ms(10),
  },
  bulletPoint: {
    fontSize: ms(14, 0.3),
    color: '#4B5563',
    lineHeight: ms(22, 0.3),
    marginBottom: ms(6),
    marginLeft: ms(10),
  },
  bold: {
    fontWeight: '600',
    color: '#1C2C4F',
  },
  contactInfo: {
    fontSize: ms(14, 0.3),
    color: '#29BFC0',
    lineHeight: ms(22, 0.3),
    marginBottom: ms(4),
  },
  contactLink: {
    textDecorationLine: 'underline',
  },
  bottomPadding: {
    height: s(40),
  },
});

export default PrivacyPolicyScreen;
